/**
 * Wall-clock timestamp helpers — single source of truth for the house
 * convention on appointment stamps.
 *
 * THE CONVENTION: an appointment's Pacific WALL CLOCK is stored stamped as
 * UTC. A 7:00 AM appointment is `2026-09-04T07:00:00+00:00` in the DB and
 * must render "7:00 AM" for every viewer, in every browser timezone. The
 * stamp is NOT a real instant — the "+00:00" is a carrier, not a claim
 * about the moment in time. (See `_book_network_appointment`, which builds
 * it as `datetime.combine(date, time).replace(tzinfo=timezone.utc)`.)
 *
 * WHY this file exists (2026-09-01 production report): the global patient
 * search rendered these stamps with `new Date(iso).toLocaleString()`, which
 * treats the carrier as truth and converts. A 7:00 AM appointment showed as
 * 2:00 AM to a Central-time viewer (−5) — and early-morning stamps rolled
 * back to the previous DAY. The CRM queue was correct because it stripped
 * the offset first, but that fix lived as a private function in one page,
 * so every other render site was free to get it wrong.
 *
 * MECHANISM: format the instant with `timeZone: "UTC"` pinned. That reads
 * the stamp's own clock face back verbatim and is immune to the viewer's
 * zone AND to DST (the older strip-the-offset-then-parse-local trick
 * breaks on the spring-forward hour, where 02:30 local does not exist).
 *
 * NOT for true instants — `created_at`, `submitted_at`, `callback_at`,
 * `locked_at`, audit timestamps. Those are real moments and converting them
 * to the viewer's zone is CORRECT; keep using plain `toLocaleString`.
 * NOT for date-only fields (`date_of_birth`, `date_of_injury`) — see
 * `lib/dateOnly.js`.
 */

const NULL_LABEL = "—";

export const DATETIME_OPTS = {
  month: "short", day: "numeric", year: "numeric",
  hour: "numeric", minute: "2-digit",
};
export const DATE_OPTS = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
export const TIME_OPTS = { hour: "numeric", minute: "2-digit" };

// A wall-clock stamp may arrive with an explicit offset ("…+00:00" / "…Z")
// or bare ("2026-09-04T07:00:00"). Bare strings would parse as LOCAL, so
// they get the carrier appended before parsing — after which every value
// is read back through timeZone: "UTC".
export function parseWallClock(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const hasZone = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(s);
  const d = new Date(hasZone ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatWallClock(value, options = DATETIME_OPTS, { locale = "en-US", nullLabel = NULL_LABEL } = {}) {
  const d = parseWallClock(value);
  if (!d) return nullLabel;
  return d.toLocaleString(locale, { ...options, timeZone: "UTC" });
}

// "Sep 4, 2026, 7:00 AM"
export function formatWallClockDateTime(value, opts) {
  return formatWallClock(value, DATETIME_OPTS, opts);
}

// "Fri, Sep 4, 2026"
export function formatWallClockDate(value, opts) {
  return formatWallClock(value, DATE_OPTS, opts);
}

// "7:00 AM"
export function formatWallClockTime(value, opts) {
  return formatWallClock(value, TIME_OPTS, opts);
}

// Comparing a wall-clock stamp against `Date.now()` is the same bug in
// arithmetic form (it would skew past/upcoming by the viewer's offset).
// Compare wall clock against wall clock: "now" as seen on a Pacific clock.
export function pacificNowWallClockMs(now = new Date()) {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(now).reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
  const hour = p.hour === "24" ? "00" : p.hour;
  return Date.parse(`${p.year}-${p.month}-${p.day}T${hour}:${p.minute}:${p.second}Z`);
}

export function isWallClockPast(value, now = new Date()) {
  const d = parseWallClock(value);
  if (!d) return false;
  return d.getTime() < pacificNowWallClockMs(now);
}
